import React, { useState, useEffect, useRef } from "react";
import { jwtDecode } from "jwt-decode";
import { usePostForm } from "../../../context/PostFormContext";
import useGetAllUsers from "../../../hooks/useGetAllUsers";
import useGetAllHashTag from "../../../hooks/useGetAllHashTagList";
import useGetUserPoint from "../../../hooks/useGetUserPoint";
import useCanGiveCoins from "../../../hooks/useCanGiveCoins";
import {
  CreateUserProps,
  TokenPayload,
  HashTagListProps,
} from "../../../types/ComponentProps";
import MentionInputStyles from "./MentionInputStyles.module.css";
import RecipientAvatar from "../../recipientButton/RecipientAvatar";

type DropdownMode = "mention" | "hashtag" | "points" | "coins" | null;

const PRESET_AMOUNTS = [5, 10, 15, 20, 25];
const PRESET_COINS = [5, 10, 25, 50, 100];

export type MentionInputHandle = {
  appendMentionChip: (user: CreateUserProps) => void;
  appendHashtagChip: (hashtag: HashTagListProps) => void;
  appendPointsChip: (amount: number) => void;
};

const MentionInput = React.forwardRef<MentionInputHandle>((_, ref) => {
  const accessToken = localStorage.getItem("accessTokenGC");
  const userDetails = accessToken ? jwtDecode<TokenPayload>(accessToken) : null;
  const { formPost, setFormPost } = usePostForm();
  const [suggestions, setSuggestions] = useState<CreateUserProps[]>([]);
  const [hashtagSuggestions, setHashtagSuggestions] = useState<
    HashTagListProps[]
  >([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [dropdownMode, setDropdownMode] = useState<DropdownMode>(null);
  const [dropdownPos, setDropdownPos] = useState<{ left: number; top: number }>(
    { left: 0, top: 0 },
  );
  const [customAmount, setCustomAmount] = useState<string>("");
  const [customCoins, setCustomCoins] = useState<string>("");
  const [activeIndex, setActiveIndex] = useState<number>(-1);
  const [usersState, setUserState] = useState<CreateUserProps[]>([]);
  const selectedUsersRef = useRef<CreateUserProps[]>([]);
  const isInternalUpdate = useRef(false);
  const editorRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const savedSelectionRef = useRef<Range | null>(null);

  const { getAllUsersData, isLoading } = useGetAllUsers();
  const { getAllHashTagListData } = useGetAllHashTag();
  const { data: userPointData } = useGetUserPoint();
  const canGiveCoins = useCanGiveCoins();

  // Scroll active item into view
  useEffect(() => {
    if (activeIndex >= 0 && dropdownRef.current) {
      const prefix =
        dropdownMode === "mention"
          ? "post-mention-option"
          : dropdownMode === "hashtag"
            ? "post-hashtag-option"
            : dropdownMode === "coins"
              ? "post-coins-option"
              : "post-points-option";
      const activeElement = dropdownRef.current.querySelector(
        `#${prefix}-${activeIndex}`,
      );
      if (activeElement) {
        activeElement.scrollIntoView({ block: "nearest" });
      }
    }
  }, [activeIndex, dropdownMode]);

  // Expose imperative methods for external chip insertion (from header buttons)
  React.useImperativeHandle(ref, () => ({
    appendMentionChip: (user: CreateUserProps) => {
      appendChipAtEnd(createMentionChipElement(user));
      if (!selectedUsersRef.current.some((u) => u.id === user.id)) {
        selectedUsersRef.current = [...selectedUsersRef.current, user];
      }
      syncFormFromEditor();
    },
    appendHashtagChip: (hashtag: HashTagListProps) => {
      appendChipAtEnd(createHashtagChipElement(hashtag));
      isInternalUpdate.current = true;
      setFormPost((prev) => {
        const existing = prev.hashTagIds || [];
        const alreadyExists = existing.some(
          (item: any) => item.hashTagId === hashtag.hashTagId,
        );
        if (!alreadyExists) {
          return {
            ...prev,
            hashTagIds: [
              ...existing,
              {
                hashTagId: hashtag.hashTagId,
                hashTagName: hashtag.hashTagName,
              },
            ],
          };
        }
        return prev;
      });
      syncFormFromEditor();
    },
    appendPointsChip: (amount: number) => {
      if (editorRef.current) {
        const existingChip = editorRef.current.querySelector("[data-points]");
        if (existingChip) existingChip.remove();
      }
      appendChipAtEnd(createPointsChipElement(amount));
      isInternalUpdate.current = true;
      setFormPost((prev) => ({
        ...prev,
        pointsGiven: amount,
      }));
      syncFormFromEditor();
    },
  }));

  // Reset editor when formPost.message is cleared (e.g. after submission)
  useEffect(() => {
    if (!formPost.message && editorRef.current) {
      editorRef.current.innerHTML = "";
      selectedUsersRef.current = [];
    }
  }, [formPost.message]);

  // Sync selectedUsersRef when recipients are removed externally
  useEffect(() => {
    if (isInternalUpdate.current) {
      isInternalUpdate.current = false;
      return;
    }
    const externalRecipients = formPost.recipients || [];
    const currentIds = selectedUsersRef.current.map((u) => u.id);
    const externalIds = externalRecipients.map((u) => u.id);

    if (
      currentIds.length > externalIds.length &&
      externalIds.every((id) => currentIds.includes(id))
    ) {
      selectedUsersRef.current = selectedUsersRef.current.filter((u) =>
        externalIds.includes(u.id),
      );

      if (editorRef.current) {
        const chips = editorRef.current.querySelectorAll(`[data-mention]`);
        chips.forEach((chip) => {
          const userId = Number(chip.getAttribute("data-user-id"));
          if (!externalIds.includes(userId)) {
            chip.remove();
          }
        });
        syncFormFromEditor();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formPost.recipients]);

  useEffect(() => {
    if (getAllUsersData?.users?.length) {
      const users = getAllUsersData.users
        .filter((user: any) => user.id !== userDetails?.id)
        .map((user: any) => ({
          ...user,
          nickname: `${user.preferredName?.replace(/\s+/g, "")}`,
        }));
      setUserState(users);
    }
  }, [getAllUsersData, userDetails?.id]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node) &&
        editorRef.current &&
        !editorRef.current.contains(e.target as Node)
      ) {
        setShowDropdown(false);
        setDropdownMode(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const getPlainText = (): string => {
    if (!editorRef.current) return "";
    let text = "";
    editorRef.current.childNodes.forEach((node) => {
      if (node.nodeType === Node.TEXT_NODE) {
        text += node.textContent || "";
      } else if (node instanceof HTMLElement && node.dataset.mention) {
        text += `@${node.dataset.nickname}`;
      } else if (node instanceof HTMLElement && node.dataset.hashtag) {
        text += `#${node.getAttribute("data-hashtag-name") || ""}`;
      } else if (node instanceof HTMLElement && node.dataset.points) {
        text += `+${node.dataset.amount || "0"}`;
      } else if (node instanceof HTMLElement && node.dataset.coins) {
        text += `$${node.dataset.coinAmount || "0"}`;
      } else if (node instanceof HTMLElement) {
        text += node.textContent || "";
      }
    });
    return text;
  };

  const getSelectedUsers = (): CreateUserProps[] => {
    if (!editorRef.current) return [];
    const chips = editorRef.current.querySelectorAll("[data-mention]");
    const userIds = Array.from(chips).map((chip) =>
      Number(chip.getAttribute("data-user-id")),
    );
    return selectedUsersRef.current.filter((u) =>
      userIds.includes(Number(u.id)),
    );
  };

  const syncFormFromEditor = () => {
    const plainText = getPlainText();
    const users = getSelectedUsers();
    selectedUsersRef.current = users;

    // Get coinsGiven from editor
    let coinsGiven = 0;
    if (editorRef.current) {
      const coinsChip = editorRef.current.querySelector("[data-coins]");
      if (coinsChip) {
        coinsGiven = Number(coinsChip.getAttribute("data-coin-amount")) || 0;
      }
    }

    isInternalUpdate.current = true;
    setFormPost((prev) => ({
      ...prev,
      recipients: users,
      message: plainText,
      coinsGiven,
    }));
  };

  const createMentionChipElement = (user: CreateUserProps): HTMLElement => {
    const nickname = user.preferredName.replace(/\s+/g, "");
    const chip = document.createElement("span");
    chip.className = MentionInputStyles.mentionChip;
    chip.contentEditable = "false";
    chip.dataset.mention = "true";
    chip.dataset.userId = String(user.id);
    chip.dataset.nickname = nickname;
    chip.textContent = `@${user.preferredName}`;
    return chip;
  };

  const createHashtagChipElement = (hashtag: HashTagListProps): HTMLElement => {
    const chip = document.createElement("span");
    chip.className = MentionInputStyles.hashtagChip;
    chip.contentEditable = "false";
    chip.dataset.hashtag = "true";
    chip.dataset.hashtagId = String(hashtag.hashTagId);
    chip.dataset.hashtagName = hashtag.hashTagName || "";
    chip.textContent = `#${hashtag.hashTagName}`;
    return chip;
  };

  const createPointsChipElement = (amount: number): HTMLElement => {
    const chip = document.createElement("span");
    chip.className = MentionInputStyles.pointsChip;
    chip.contentEditable = "false";
    chip.dataset.points = "true";
    chip.dataset.amount = String(amount);
    chip.textContent = `+${amount}`;
    return chip;
  };

  const appendChipAtEnd = (chip: HTMLElement) => {
    if (!editorRef.current) return;

    if (savedSelectionRef.current) {
      editorRef.current.focus();
      const selection = window.getSelection();
      if (selection) {
        selection.removeAllRanges();
        selection.addRange(savedSelectionRef.current);

        const range = selection.getRangeAt(0);
        range.deleteContents();
        range.insertNode(chip);

        const space = document.createTextNode(" ");
        chip.after(space);

        const newRange = document.createRange();
        newRange.setStartAfter(space);
        newRange.collapse(true);
        selection.removeAllRanges();
        selection.addRange(newRange);
        savedSelectionRef.current = newRange.cloneRange();
        return;
      }
    }

    editorRef.current.appendChild(chip);
    const space = document.createTextNode(" ");
    editorRef.current.appendChild(space);
    editorRef.current.focus();
    const selection = window.getSelection();
    if (selection) {
      const range = document.createRange();
      range.setStartAfter(space);
      range.collapse(true);
      selection.removeAllRanges();
      selection.addRange(range);
    }
  };

  const getCaretPosition = (): { left: number; top: number } => {
    const selection = window.getSelection();
    if (!selection || !selection.rangeCount || !editorRef.current)
      return { left: 0, top: 0 };

    const range = selection.getRangeAt(0).cloneRange();
    range.collapse(true);

    const rect = range.getClientRects()[0];
    if (!rect) return { left: 0, top: 0 };

    const editorRect = editorRef.current.getBoundingClientRect();
    return {
      left: rect.left - editorRect.left,
      top: rect.bottom - editorRect.top + 4,
    };
  };

  const getCurrentTrigger = (): {
    trigger: DropdownMode;
    query: string;
  } | null => {
    const selection = window.getSelection();
    if (!selection || !selection.rangeCount) return null;

    const range = selection.getRangeAt(0);
    const node = range.startContainer;

    if (node.nodeType !== Node.TEXT_NODE) return null;

    const text = node.textContent || "";
    const cursorPos = range.startOffset;
    const textBeforeCursor = text.slice(0, cursorPos);

    const atMatch = textBeforeCursor.match(/@(\S*)$/);
    if (atMatch) return { trigger: "mention", query: atMatch[1] };

    const hashMatch = textBeforeCursor.match(/#(\S*)$/);
    if (hashMatch) return { trigger: "hashtag", query: hashMatch[1] };

    const plusMatch = textBeforeCursor.match(/\+(\S*)$/);
    if (plusMatch) return { trigger: "points", query: plusMatch[1] };

    // Only trigger coins dropdown if user is authorized to give coins
    const dollarMatch = textBeforeCursor.match(/\$(\S*)$/);
    if (dollarMatch && canGiveCoins) return { trigger: "coins", query: dollarMatch[1] };

    return null;
  };

  const handleInput = () => {
    const selection = window.getSelection();
    if (selection && selection.rangeCount > 0) {
      savedSelectionRef.current = selection.getRangeAt(0).cloneRange();
    }

    const triggerInfo = getCurrentTrigger();

    if (triggerInfo) {
      const { trigger, query } = triggerInfo;
      setDropdownMode(trigger);
      setDropdownPos(getCaretPosition());
      setShowDropdown(true);
      setActiveIndex(-1);

      if (trigger === "mention") {
        const alreadySelectedIds = selectedUsersRef.current.map((u) => u.id);
        const lowerQuery = query.toLowerCase();
        const filtered = usersState.filter((user) => {
          const isAlreadySelected = alreadySelectedIds.includes(user.id);
          const matchesQuery =
            user.preferredName.toLowerCase().startsWith(lowerQuery) ||
            user.firstName.toLowerCase().startsWith(lowerQuery) ||
            user.lastName.toLowerCase().startsWith(lowerQuery);
          return matchesQuery && !isAlreadySelected;
        });
        setSuggestions(filtered);
      } else if (trigger === "hashtag") {
        const lowerQuery = query.toLowerCase();
        const selectedHashTagIds = new Set(
          (formPost.hashTagIds || []).map((tag: any) => tag.hashTagId),
        );
        const allHashtags = getAllHashTagListData?.hashTagList || [];
        const filtered = allHashtags.filter(
          (tag: any) =>
            !selectedHashTagIds.has(tag.hashTagId) &&
            tag.hashTagName.toLowerCase().startsWith(lowerQuery),
        );
        setHashtagSuggestions(filtered);
      } else if (trigger === "points") {
        if (editorRef.current?.querySelector("[data-points]")) {
          setShowDropdown(false);
          setDropdownMode(null);
        } else {
          setCustomAmount(query);
        }
      } else if (trigger === "coins") {
        // Double-check authorization for coins
        if (!canGiveCoins) {
          setShowDropdown(false);
          setDropdownMode(null);
        } else if (editorRef.current?.querySelector("[data-coins]")) {
          setShowDropdown(false);
          setDropdownMode(null);
        } else {
          setCustomCoins(query);
        }
      }
    } else {
      setShowDropdown(false);
      setDropdownMode(null);
      setActiveIndex(-1);
    }

    syncFormFromEditor();
  };

  const removeChip = (chip: HTMLElement) => {
    if (chip.dataset.hashtag) {
      const hashTagId = Number(chip.getAttribute("data-hashtag-id"));
      setFormPost((prevForm) => ({
        ...prevForm,
        hashTagIds: (prevForm.hashTagIds || []).filter(
          (t: any) => t.hashTagId !== hashTagId,
        ),
      }));
    }
    if (chip.dataset.points) {
      setFormPost((prevForm) => ({
        ...prevForm,
        pointsGiven: 0,
      }));
    }
    chip.remove();
    syncFormFromEditor();
  };

  const isChipNode = (node: any): boolean => {
    return (
      node instanceof HTMLElement &&
      !!(node.dataset.mention || node.dataset.hashtag || node.dataset.points || node.dataset.coins)
    );
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    // Keyboard navigation for dropdown
    if (showDropdown) {
      if (dropdownMode === "mention" && suggestions.length > 0) {
        if (e.key === "ArrowDown") { e.preventDefault(); setActiveIndex((p) => p < suggestions.length - 1 ? p + 1 : 0); return; }
        if (e.key === "ArrowUp") { e.preventDefault(); setActiveIndex((p) => p > 0 ? p - 1 : suggestions.length - 1); return; }
        if (e.key === "Enter" || e.key === " ") { if (activeIndex >= 0) { e.preventDefault(); insertMention(suggestions[activeIndex]); return; } }
        if (e.key === "Escape") { e.preventDefault(); setShowDropdown(false); setDropdownMode(null); setActiveIndex(-1); return; }
      }
      if (dropdownMode === "hashtag" && hashtagSuggestions.length > 0) {
        if (e.key === "ArrowDown") { e.preventDefault(); setActiveIndex((p) => p < hashtagSuggestions.length - 1 ? p + 1 : 0); return; }
        if (e.key === "ArrowUp") { e.preventDefault(); setActiveIndex((p) => p > 0 ? p - 1 : hashtagSuggestions.length - 1); return; }
        if (e.key === "Enter" || e.key === " ") { if (activeIndex >= 0) { e.preventDefault(); insertHashtag(hashtagSuggestions[activeIndex]); return; } }
        if (e.key === "Escape") { e.preventDefault(); setShowDropdown(false); setDropdownMode(null); setActiveIndex(-1); return; }
      }
      if (dropdownMode === "points") {
        if (e.key === "ArrowDown") { e.preventDefault(); setActiveIndex((p) => p < PRESET_AMOUNTS.length - 1 ? p + 1 : 0); return; }
        if (e.key === "ArrowUp") { e.preventDefault(); setActiveIndex((p) => p > 0 ? p - 1 : PRESET_AMOUNTS.length - 1); return; }
        if (e.key === "Enter" || e.key === " ") { if (activeIndex >= 0) { e.preventDefault(); insertPoints(PRESET_AMOUNTS[activeIndex]); return; } }
        if (e.key === "Escape") { e.preventDefault(); setShowDropdown(false); setDropdownMode(null); setActiveIndex(-1); return; }
      }
      if (dropdownMode === "coins") {
        if (e.key === "ArrowDown") { e.preventDefault(); setActiveIndex((p) => p < PRESET_COINS.length - 1 ? p + 1 : 0); return; }
        if (e.key === "ArrowUp") { e.preventDefault(); setActiveIndex((p) => p > 0 ? p - 1 : PRESET_COINS.length - 1); return; }
        if (e.key === "Enter" || e.key === " ") { if (activeIndex >= 0) { e.preventDefault(); insertCoins(PRESET_COINS[activeIndex]); return; } }
        if (e.key === "Escape") { e.preventDefault(); setShowDropdown(false); setDropdownMode(null); setActiveIndex(-1); return; }
      }
    }

    if (e.key === "Enter" && !showDropdown) { e.preventDefault(); }

    if (e.key === "Backspace") {
      const selection = window.getSelection();
      if (!selection || !selection.rangeCount) return;
      const range = selection.getRangeAt(0);
      if (!range.collapsed) return;
      const node = range.startContainer;
      const offset = range.startOffset;

      if (node.nodeType === Node.TEXT_NODE && offset === 0) {
        const prev = node.previousSibling;
        if (isChipNode(prev)) { e.preventDefault(); removeChip(prev as HTMLElement); return; }
      }
      if (node === editorRef.current && offset > 0) {
        const childBefore = node.childNodes[offset - 1];
        if (isChipNode(childBefore)) { e.preventDefault(); removeChip(childBefore as HTMLElement); return; }
      }
      if (node === editorRef.current && offset === 0) { return; }
    }

    if (e.key === "Delete") {
      const selection = window.getSelection();
      if (!selection || !selection.rangeCount) return;
      const range = selection.getRangeAt(0);
      if (!range.collapsed) return;
      const node = range.startContainer;
      const offset = range.startOffset;

      if (node === editorRef.current) {
        const childAfter = node.childNodes[offset];
        if (isChipNode(childAfter)) { e.preventDefault(); removeChip(childAfter as HTMLElement); return; }
      }
      if (node.nodeType === Node.TEXT_NODE && offset === (node.textContent || "").length) {
        const next = node.nextSibling;
        if (isChipNode(next)) { e.preventDefault(); removeChip(next as HTMLElement); return; }
      }
    }
  };

  const replaceTriggerText = (chipElement: HTMLElement) => {
    const selection = window.getSelection();
    if (!selection || !selection.rangeCount || !editorRef.current) return;

    const range = selection.getRangeAt(0);
    const node = range.startContainer;

    if (node.nodeType !== Node.TEXT_NODE) return;

    const text = node.textContent || "";
    const cursorPos = range.startOffset;
    const textBeforeCursor = text.slice(0, cursorPos);
    const textAfterCursor = text.slice(cursorPos);

    let triggerIndex = -1;
    const triggers = ["@", "#", "+", "$"];
    for (const t of triggers) {
      const idx = textBeforeCursor.lastIndexOf(t);
      if (idx > triggerIndex) triggerIndex = idx;
    }
    if (triggerIndex === -1) return;

    const beforeTrigger = textBeforeCursor.slice(0, triggerIndex);
    const parent = node.parentNode;
    if (!parent) return;

    const beforeNode = document.createTextNode(beforeTrigger);
    const afterNode = document.createTextNode(` ${textAfterCursor}`);

    parent.replaceChild(afterNode, node);
    parent.insertBefore(chipElement, afterNode);
    parent.insertBefore(beforeNode, chipElement);

    const newRange = document.createRange();
    newRange.setStart(afterNode, 1);
    newRange.collapse(true);
    selection.removeAllRanges();
    selection.addRange(newRange);
  };

  const insertMention = (user: CreateUserProps) => {
    const nickname = user.preferredName.replace(/\s+/g, "");
    const chip = document.createElement("span");
    chip.className = MentionInputStyles.mentionChip;
    chip.contentEditable = "false";
    chip.dataset.mention = "true";
    chip.dataset.userId = String(user.id);
    chip.dataset.nickname = nickname;
    chip.textContent = `@${user.preferredName}`;
    replaceTriggerText(chip);
    if (!selectedUsersRef.current.some((u) => u.id === user.id)) {
      selectedUsersRef.current = [...selectedUsersRef.current, user];
    }
    setShowDropdown(false);
    setDropdownMode(null);
    syncFormFromEditor();
  };

  const insertHashtag = (hashtag: HashTagListProps) => {
    const chip = document.createElement("span");
    chip.className = MentionInputStyles.hashtagChip;
    chip.contentEditable = "false";
    chip.dataset.hashtag = "true";
    chip.dataset.hashtagId = String(hashtag.hashTagId);
    chip.dataset.hashtagName = hashtag.hashTagName || "";
    chip.textContent = `#${hashtag.hashTagName}`;
    replaceTriggerText(chip);
    isInternalUpdate.current = true;
    setFormPost((prev) => {
      const existing = prev.hashTagIds || [];
      const alreadyExists = existing.some(
        (item: any) => item.hashTagId === hashtag.hashTagId,
      );
      if (!alreadyExists) {
        return {
          ...prev,
          hashTagIds: [
            ...existing,
            { hashTagId: hashtag.hashTagId, hashTagName: hashtag.hashTagName },
          ],
        };
      }
      return prev;
    });
    setShowDropdown(false);
    setDropdownMode(null);
    syncFormFromEditor();
  };

  const insertPoints = (amount: number) => {
    if (editorRef.current) {
      const existingChip = editorRef.current.querySelector("[data-points]");
      if (existingChip) existingChip.remove();
    }
    if (savedSelectionRef.current && editorRef.current) {
      editorRef.current.focus();
      const selection = window.getSelection();
      if (selection) {
        selection.removeAllRanges();
        selection.addRange(savedSelectionRef.current);
      }
    }
    const chip = document.createElement("span");
    chip.className = MentionInputStyles.pointsChip;
    chip.contentEditable = "false";
    chip.dataset.points = "true";
    chip.dataset.amount = String(amount);
    chip.textContent = `+${amount}`;
    replaceTriggerText(chip);
    isInternalUpdate.current = true;
    setFormPost((prev) => ({
      ...prev,
      pointsGiven: amount,
    }));
    setShowDropdown(false);
    setDropdownMode(null);
    setCustomAmount("");
    syncFormFromEditor();
  };

  const insertCoins = (amount: number) => {
    if (editorRef.current) {
      const existingChip = editorRef.current.querySelector("[data-coins]");
      if (existingChip) existingChip.remove();
    }
    if (savedSelectionRef.current && editorRef.current) {
      editorRef.current.focus();
      const selection = window.getSelection();
      if (selection) {
        selection.removeAllRanges();
        selection.addRange(savedSelectionRef.current);
      }
    }
    const chip = document.createElement("span");
    chip.className = MentionInputStyles.coinsChip;
    chip.contentEditable = "false";
    chip.dataset.coins = "true";
    chip.dataset.coinAmount = String(amount);
    chip.textContent = `$${amount}`;
    replaceTriggerText(chip);
    setShowDropdown(false);
    setDropdownMode(null);
    setCustomCoins("");
    syncFormFromEditor();
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLDivElement>) => {
    e.preventDefault();
    const text = e.clipboardData.getData("text/plain");
    document.execCommand("insertText", false, text);
  };

  const renderMentionDropdown = () => (
    <>
      {suggestions.length === 0 ? (
        <div className={MentionInputStyles.emptyMessage}>No users found</div>
      ) : (
        suggestions.map((user, index) => (
          <div
            key={user.id}
            id={`post-mention-option-${index}`}
            className={`${MentionInputStyles.dropdownItem} ${index === activeIndex ? MentionInputStyles.dropdownItemActive : ""}`}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => insertMention(user)}
            onMouseEnter={() => setActiveIndex(index)}
            role="option"
            aria-selected={index === activeIndex}
            aria-label={`${user.firstName} ${user.lastName}, ${user.email}`}
          >
            <div className={MentionInputStyles.userListBlk}>
              <div>
                <RecipientAvatar userId={Number(user.id)} />
              </div>
              <div className="flex flex-column">
                <h3>
                  {user.firstName} {user.lastName}
                </h3>
                <span>{user.email}</span>
              </div>
            </div>
          </div>
        ))
      )}
    </>
  );

  const renderHashtagDropdown = () => (
    <>
      {hashtagSuggestions.length === 0 ? (
        <div className={MentionInputStyles.emptyMessage}>No hashtags found</div>
      ) : (
        hashtagSuggestions.map((tag, index) => (
          <div
            key={tag.hashTagId}
            id={`post-hashtag-option-${index}`}
            className={`${MentionInputStyles.dropdownItem} ${index === activeIndex ? MentionInputStyles.dropdownItemActive : ""}`}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => insertHashtag(tag)}
            onMouseEnter={() => setActiveIndex(index)}
            role="option"
            aria-selected={index === activeIndex}
            aria-label={`Hashtag ${tag.hashTagName}`}
          >
            <span className={MentionInputStyles.hashtagSuggestionText}>
              #{tag.hashTagName}
            </span>
          </div>
        ))
      )}
    </>
  );

  const renderPointsDropdown = () => (
    <div className={MentionInputStyles.pointsDropdownContent} role="group" aria-label="Select points amount">
      <div className={MentionInputStyles.pointsPresets} role="listbox" aria-label="Preset amounts">
        {PRESET_AMOUNTS.map((amount, index) => (
          <button
            key={amount}
            id={`post-points-option-${index}`}
            className={`${MentionInputStyles.pointsPresetBtn} ${index === activeIndex ? MentionInputStyles.pointsPresetBtnActive : ""}`}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => insertPoints(amount)}
            onMouseEnter={() => setActiveIndex(index)}
            type="button"
            role="option"
            aria-selected={index === activeIndex}
            aria-label={`Give ${amount} points`}
          >
            +{amount}
          </button>
        ))}
      </div>
      <div className={MentionInputStyles.pointsCustomRow}>
        <input
          type="number"
          placeholder="Enter specific amount"
          className={MentionInputStyles.pointsCustomInput}
          value={customAmount}
          onMouseDown={(e) => e.stopPropagation()}
          onFocus={() => {
            const selection = window.getSelection();
            if (
              selection &&
              selection.rangeCount > 0 &&
              editorRef.current?.contains(selection.anchorNode)
            ) {
              savedSelectionRef.current = selection.getRangeAt(0).cloneRange();
            }
          }}
          onChange={(e) => setCustomAmount(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && customAmount) {
              e.preventDefault();
              const parsed = parseInt(customAmount, 10);
              if (!isNaN(parsed) && parsed > 0) insertPoints(parsed);
            }
            if (["e", "E", "-"].includes(e.key)) e.preventDefault();
          }}
        />
        <button
          className={MentionInputStyles.pointsOkBtn}
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => {
            const parsed = parseInt(customAmount, 10);
            if (!isNaN(parsed) && parsed > 0) insertPoints(parsed);
          }}
          type="button"
        >
          OK
        </button>
      </div>
      <p className={MentionInputStyles.pointsAvailable}>
        <span>{userPointData?.points?.giveablePoints || 0}</span> Points
        available to give
      </p>
    </div>
  );

  const renderCoinsDropdown = () => (
    <div className={MentionInputStyles.pointsDropdownContent} role="group" aria-label="Select coins amount">
      <div className={MentionInputStyles.pointsPresets} role="listbox" aria-label="Preset coin amounts">
        {PRESET_COINS.map((amount, index) => (
          <button
            key={amount}
            id={`post-coins-option-${index}`}
            className={`${MentionInputStyles.coinsPresetBtn} ${index === activeIndex ? MentionInputStyles.pointsPresetBtnActive : ""}`}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => insertCoins(amount)}
            onMouseEnter={() => setActiveIndex(index)}
            type="button"
            role="option"
            aria-selected={index === activeIndex}
            aria-label={`Give ${amount} coins`}
          >
            ${amount}
          </button>
        ))}
      </div>
      <div className={MentionInputStyles.pointsCustomRow}>
        <input
          type="number"
          placeholder="Enter specific coins"
          className={MentionInputStyles.pointsCustomInput}
          value={customCoins}
          onMouseDown={(e) => e.stopPropagation()}
          onFocus={() => {
            const selection = window.getSelection();
            if (
              selection &&
              selection.rangeCount > 0 &&
              editorRef.current?.contains(selection.anchorNode)
            ) {
              savedSelectionRef.current = selection.getRangeAt(0).cloneRange();
            }
          }}
          onChange={(e) => setCustomCoins(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && customCoins) {
              e.preventDefault();
              const parsed = parseInt(customCoins, 10);
              if (!isNaN(parsed) && parsed > 0) insertCoins(parsed);
            }
            if (["e", "E", "-"].includes(e.key)) e.preventDefault();
          }}
        />
        <button
          className={MentionInputStyles.pointsOkBtn}
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => {
            const parsed = parseInt(customCoins, 10);
            if (!isNaN(parsed) && parsed > 0) insertCoins(parsed);
          }}
          type="button"
        >
          OK
        </button>
      </div>
      <p className={MentionInputStyles.pointsAvailable}>
        <span>{userPointData?.points?.coins || 0}</span> Coins available
      </p>
    </div>
  );

  return (
    <div className={MentionInputStyles.mentionContainer}>
      <div className={MentionInputStyles.mentionWrapper}>
        <div
          ref={editorRef}
          className={MentionInputStyles.mentionEditor}
          contentEditable
          onInput={handleInput}
          onKeyDown={handleKeyDown}
          onPaste={handlePaste}
          onBlur={() => {
            const selection = window.getSelection();
            if (
              selection &&
              selection.rangeCount > 0 &&
              editorRef.current?.contains(selection.anchorNode)
            ) {
              savedSelectionRef.current = selection.getRangeAt(0).cloneRange();
            }
          }}
          data-placeholder={isLoading ? "Loading...." : "What's on your mind?"}
          suppressContentEditableWarning
          role="combobox"
          aria-label="Write a post"
          aria-multiline="true"
          aria-expanded={showDropdown}
          aria-haspopup="listbox"
          aria-controls={showDropdown ? "post-suggestions-listbox" : undefined}
          aria-activedescendant={
            showDropdown && activeIndex >= 0
              ? dropdownMode === "mention"
                ? `post-mention-option-${activeIndex}`
                : dropdownMode === "hashtag"
                  ? `post-hashtag-option-${activeIndex}`
                  : dropdownMode === "coins"
                    ? `post-coins-option-${activeIndex}`
                    : `post-points-option-${activeIndex}`
              : undefined
          }
        />
        {showDropdown && (
          <div
            ref={dropdownRef}
            id="post-suggestions-listbox"
            className={MentionInputStyles.dropdown}
            style={{ left: dropdownPos.left, top: dropdownPos.top }}
            role="listbox"
            aria-label={
              dropdownMode === "mention"
                ? "User suggestions"
                : dropdownMode === "hashtag"
                  ? "Hashtag suggestions"
                  : dropdownMode === "coins"
                    ? "Coins options"
                    : "Points options"
            }
          >
            {dropdownMode === "mention" && renderMentionDropdown()}
            {dropdownMode === "hashtag" && renderHashtagDropdown()}
            {dropdownMode === "points" && renderPointsDropdown()}
            {dropdownMode === "coins" && renderCoinsDropdown()}
          </div>
        )}
      </div>
    </div>
  );
});

export default MentionInput;
